export type SoundSourceType = 'crowd-light' | 'crowd-medium' | 'crowd-heavy';

export interface SoundScenarioConfig {
  src: SoundSourceType;
  dbBare: number;
  dbTreated: number;
}

type AudioNodes = {
  ctx: AudioContext;
  master: GainNode;
  treatFilter: BiquadFilterNode;
  treatGain: GainNode;
  roomGain?: GainNode;
  sources: AudioScheduledSourceNode[];
};

const CROWD_PROFILES: Record<
  SoundSourceType,
  { voices: number; level: number; room: number; reverb: number }
> = {
  'crowd-light': { voices: 7, level: 0.42, room: 0.08, reverb: 0.12 },
  'crowd-medium': { voices: 11, level: 0.52, room: 0.1, reverb: 0.18 },
  'crowd-heavy': { voices: 15, level: 0.62, room: 0.12, reverb: 0.24 },
};

function createPinkNoiseBuffer(ctx: AudioContext, seconds = 3) {
  const length = Math.floor(ctx.sampleRate * seconds);
  const buffer = ctx.createBuffer(1, length, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  let state = 0;

  for (let i = 0; i < length; i += 1) {
    const white = Math.random() * 2 - 1;
    state = (state + 0.02 * white) / 1.02;
    data[i] = state * 3.2;
  }

  return buffer;
}

function connectAmplitudeModulator(
  ctx: AudioContext,
  gain: GainNode,
  depth: number,
  rate: number,
  sources: AudioScheduledSourceNode[],
) {
  const offset = ctx.createConstantSource();
  offset.offset.value = 0.18 + depth * 0.55;

  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = rate;

  const lfoGain = ctx.createGain();
  lfoGain.gain.value = depth;

  offset.connect(gain.gain);
  lfo.connect(lfoGain);
  lfoGain.connect(gain.gain);

  offset.start();
  lfo.start();
  sources.push(offset, lfo);
}

function addVoiceLayer(
  ctx: AudioContext,
  destination: AudioNode,
  profile: (typeof CROWD_PROFILES)[SoundSourceType],
  index: number,
  sources: AudioScheduledSourceNode[],
) {
  const noise = ctx.createBufferSource();
  noise.buffer = createPinkNoiseBuffer(ctx, 2.4 + (index % 3) * 0.4);
  noise.loop = true;

  const formants = [
    [320, 820, 2100],
    [380, 980, 2400],
    [420, 1100, 2600],
    [300, 900, 2200],
    [360, 1050, 2500],
    [400, 1200, 2800],
  ][index % 6];

  const voiceBus = ctx.createGain();
  voiceBus.gain.value = profile.level * (0.55 + (index % 4) * 0.08);

  formants.forEach((freq, formantIndex) => {
    const band = ctx.createBiquadFilter();
    band.type = 'bandpass';
    band.frequency.value = freq + (index % 5) * 18;
    band.Q.value = formantIndex === 1 ? 2.4 : 1.4;

    const formantGain = ctx.createGain();
    formantGain.gain.value = formantIndex === 1 ? 0.55 : 0.32;

    noise.connect(band);
    band.connect(formantGain);
    formantGain.connect(voiceBus);
  });

  const voiceGain = ctx.createGain();
  voiceGain.gain.value = 1;
  voiceBus.connect(voiceGain);

  connectAmplitudeModulator(
    ctx,
    voiceGain,
    0.22 + (index % 3) * 0.06,
    2.4 + (index % 7) * 0.55,
    sources,
  );

  if ('createStereoPanner' in ctx) {
    const panner = (ctx as AudioContext).createStereoPanner();
    panner.pan.value = -0.75 + (index / Math.max(profile.voices - 1, 1)) * 1.5;
    voiceGain.connect(panner);
    panner.connect(destination);
  } else {
    voiceGain.connect(destination);
  }

  noise.start();
  sources.push(noise);
}

function startCrowdBabble(
  ctx: AudioContext,
  master: GainNode,
  src: SoundSourceType,
): { sources: AudioScheduledSourceNode[]; roomGain?: GainNode } {
  const profile = CROWD_PROFILES[src];
  const sources: AudioScheduledSourceNode[] = [];
  const crowdBus = ctx.createGain();
  crowdBus.gain.value = 1;
  crowdBus.connect(master);

  for (let i = 0; i < profile.voices; i += 1) {
    addVoiceLayer(ctx, crowdBus, profile, i, sources);
  }

  const roomNoise = ctx.createBufferSource();
  roomNoise.buffer = createPinkNoiseBuffer(ctx, 4);
  roomNoise.loop = true;

  const roomLow = ctx.createBiquadFilter();
  roomLow.type = 'lowpass';
  roomLow.frequency.value = 420;

  const roomGain = ctx.createGain();
  roomGain.gain.value = profile.room;

  roomNoise.connect(roomLow);
  roomLow.connect(roomGain);
  roomGain.connect(master);
  roomNoise.start();
  sources.push(roomNoise);

  if (profile.reverb > 0) {
    const delay = ctx.createDelay();
    delay.delayTime.value = 0.07 + profile.reverb * 0.08;

    const feedback = ctx.createGain();
    feedback.gain.value = profile.reverb;

    const reverbSend = ctx.createGain();
    reverbSend.gain.value = 0.35;

    crowdBus.connect(reverbSend);
    reverbSend.connect(delay);
    delay.connect(feedback);
    feedback.connect(delay);
    delay.connect(master);
  }

  return { sources, roomGain };
}

export function createSoundEngine(config: SoundScenarioConfig): AudioNodes | null {
  if (typeof window === 'undefined') return null;

  const Ctx =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctx) return null;

  const ctx = new Ctx();
  const treatFilter = ctx.createBiquadFilter();
  treatFilter.type = 'lowpass';
  treatFilter.frequency.value = 20000;

  const treatGain = ctx.createGain();
  treatGain.gain.value = 1;

  const master = ctx.createGain();
  master.gain.value = 0.48;

  treatFilter.connect(treatGain);
  treatGain.connect(ctx.destination);

  const started = startCrowdBabble(ctx, master, config.src);
  master.connect(treatFilter);

  return {
    ctx,
    master,
    treatFilter,
    treatGain,
    roomGain: started.roomGain,
    sources: started.sources,
  };
}

export function applyTreatment(
  nodes: AudioNodes,
  treated: boolean,
  config: SoundScenarioConfig,
) {
  const now = nodes.ctx.currentTime;
  const cutoff = treated ? 850 : 20000;

  nodes.treatFilter.frequency.setTargetAtTime(cutoff, now, 0.12);

  const gain = treated ? 2.2 * 10 ** (-(config.dbBare - config.dbTreated) / 20) : 1;
  nodes.treatGain.gain.setTargetAtTime(gain, now, 0.12);

  if (nodes.roomGain) {
    nodes.roomGain.gain.setTargetAtTime(treated ? 0.03 : CROWD_PROFILES[config.src].room, now, 0.12);
  }
}

export function stopSoundEngine(nodes: AudioNodes | null) {
  if (!nodes) return;

  nodes.sources.forEach((source) => {
    try {
      source.stop();
    } catch {
      /* already stopped */
    }
  });

  void nodes.ctx.close();
}
